/**
 * CodableAccessCard — veřejný přístup ke CodAble na tomto nodu.
 *
 * Filip 2026-08-17: CodAble má SAMOSTATNOU UI URL a SAMOSTATNOU MCP URL.
 * Chce je přepínat stejně jako služby v sekci Veřejný přístup a u UI mít
 * zapínání SSO jako u ACM dashboardu. Když nod CodAble nemá, karta místo
 * přepínačů nabídne jeho nasazení.
 *
 * MCP tooly (stejný princip jako NodeServicesCard):
 *   codable.hub_publish {action: status|publish|unpublish, target: ui|mcp}
 *   codable.hub_sso     {enabled}                  — jen UI, MCP je strojová linka
 *   toolbox.sysupdate_module {action: enable, name: codable}   — nasazení
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../lib/mcp';
import {
  AsyncStatusToggle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from './ui';
import { Bot, Copy, ExternalLink, Loader2, Shield, Terminal } from 'lucide-react';
import { toast } from 'sonner';

interface CodableHubState {
  ok?: boolean;
  node_hash?: string;
  ui_domain?: string;
  mcp_domain?: string;
  ui_url?: string | null;
  mcp_url?: string | null;
  ui_published?: boolean;
  mcp_published?: boolean;
  ui_sso_enabled?: boolean;
  ui_sso_registered?: boolean;
}

/**
 * Volání toolu se stejným kontacktem jako NodeServicesCard (přístup k ACM UI):
 * tooly často vrací { ok:false, error } bez HTTP chyby — musí se throw, jinak
 * AsyncStatusToggle zůstane na „zpracovává…" (optimistic čeká na value).
 */
async function toolCall<T extends Record<string, unknown>>(
  tool: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const r = await mcpCall<T>(tool, args);
  if (r && typeof r === 'object' && (r as { ok?: boolean }).ok === false) {
    throw new Error(String((r as { error?: string }).error || tool));
  }
  return r;
}

/**
 * Modul na nodu není → tool neexistuje. Rozlišit od skutečné chyby.
 *
 * Filip 2026-08-22: „neřekl ‚nechce mi nic říct, protože ACL', řekl ‚modul
 * tam není'" — auth/transport selhání se NIKDY nesmí vydávat za nepřítomnost.
 * Nedosažitelný nod, chybějící grant nebo scope = skutečná chyba k zobrazení,
 * ne šedá karta „modul není".
 */
function isModuleMissing(err: unknown): boolean {
  const m = String((err as Error)?.message ?? err ?? '').toLowerCase();
  if (
    m.includes('unmapped') || m.includes('grant') || m.includes('scope') ||
    m.includes('unreachable') || m.includes('forbidden') || m.includes('unauthorized') ||
    m.includes('timeout') || m.includes('nedosažiteln')
  ) return false;
  return m.includes('tool not found') || m.includes('not loaded') ||
    m.includes('unknown tool');
}

export function CodableAccessCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const queryKey = ['codable', 'hub-access'];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => mcpCall<CodableHubState>('codable.hub_publish', { action: 'status' }),
    staleTime: 5_000,
    refetchInterval: 30_000,
    retry: 0,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const togglePublish = useMutation({
    mutationFn: ({ target, enabled }: { target: 'ui' | 'mcp'; enabled: boolean }) =>
      toolCall('codable.hub_publish', {
        action: enabled ? 'publish' : 'unpublish',
        target,
      }),
    onSuccess: invalidate,
    onError: (e) => toast.error(String((e as Error)?.message ?? e)),
  });

  const toggleSso = useMutation({
    // Stejný fail-loud kontrakt jako NodeServicesCard (ACM UI SSO):
    // ok:false / vnitřní sso_result / desired bez klienta = throw,
    // jinak toggle visí na „zpracovává…" (value !== actual).
    mutationFn: async (enabled: boolean) => {
      const r = await toolCall<
        CodableHubState & {
          error?: string;
          sso_enabled?: boolean;
          sso_registered?: boolean;
          tool_result?: { sso_result?: { ok?: boolean; error?: string } };
        }
      >('codable.hub_sso', { enabled });
      const ssoRes = r.tool_result?.sso_result;
      if (enabled && ssoRes && ssoRes.ok === false) {
        throw new Error(String(ssoRes.error || t('codable.ssoRegisterFailed', 'Registrace SSO v Keycloaku selhala')));
      }
      if (enabled && r.sso_enabled && !r.sso_registered) {
        throw new Error(
          t(
            'codable.ssoFlagNoClient',
            'SSO je zapnuté, ale Keycloak klient nevznikl.',
          ),
        );
      }
      return r;
    },
    onSuccess: (r) => {
      toast.success(
        r?.sso_registered
          ? t('codable.ssoRegistered', 'SSO zaregistrováno v Keycloaku')
          : t('codable.ssoChanged', 'SSO stav změněn'),
      );
      invalidate();
    },
    onError: (e) => toast.error(String((e as Error)?.message ?? e)),
  });

  const deployCodable = useMutation({
    mutationFn: () =>
      mcpCall('toolbox.sysupdate_module', { action: 'enable', name: 'codable' }),
    onSuccess: () => {
      toast.success(
        t(
          'codable.deployQueued',
          'CodAble zapnut — projeví se po restartu acm-api (systémový update).',
        ),
      );
      invalidate();
    },
    onError: (e) => toast.error(String((e as Error)?.message ?? e)),
  });

  const copy = (v: string) => {
    navigator.clipboard?.writeText(v);
    toast.success(t('common.copied', 'Zkopírováno'));
  };

  // ── Nod CodAble nemá → nabídnout nasazení ────────────────────────────────
  if (error && isModuleMissing(error)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Bot size={16} /> {t('codable.accessTitle', 'CodAble — veřejný přístup')}
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-sm text-muted-foreground'>
            {t(
              'codable.notDeployed',
              'Tento uzel CodAble nemá. Po nasazení získá vlastní UI a MCP adresu, které tady půjde zveřejnit.',
            )}
          </p>
          <Button
            variant='primary'
            onClick={() => deployCodable.mutate()}
            disabled={deployCodable.isPending}
          >
            {deployCodable.isPending
              ? <Loader2 size={14} className='animate-spin' />
              : <Bot size={14} />}
            {t('codable.deploy', 'Nasadit CodAble')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Bot size={16} /> {t('codable.accessTitle', 'CodAble — veřejný přístup')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Loader2 size={16} className='animate-spin' />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Bot size={16} /> {t('codable.accessTitle', 'CodAble — veřejný přístup')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-red-400'>{String((error as Error)?.message ?? error)}</p>
        </CardContent>
      </Card>
    );
  }

  const st = data ?? {};
  const rows: Array<{
    key: 'ui' | 'mcp';
    icon: typeof Bot;
    label: string;
    domain?: string;
    url?: string | null;
    published: boolean;
  }> = [
    {
      key: 'ui',
      icon: Bot,
      label: t('codable.uiLabel', 'CodAble UI'),
      domain: st.ui_domain,
      url: st.ui_url,
      published: st.ui_published === true,
    },
    {
      key: 'mcp',
      icon: Terminal,
      label: t('codable.mcpLabel', 'CodAble MCP'),
      domain: st.mcp_domain,
      url: st.mcp_url,
      published: st.mcp_published === true,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <Bot size={16} /> {t('codable.accessTitle', 'CodAble — veřejný přístup')}
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        {rows.map((r) => (
          <div key={r.key} className='space-y-1'>
            <AsyncStatusToggle
              value={r.published}
              actual={r.published}
              pending={togglePublish.isPending}
              label={
                <span className='flex items-center gap-2'>
                  <r.icon size={14} /> {r.label}
                </span>
              }
              renderIdleBadge={(on) =>
                on
                  ? <span className='text-xs text-emerald-400'>{t('common.published', 'veřejné')}</span>
                  : (
                    <span className='text-xs text-muted-foreground'>
                      {t('codable.unpublished', 'nepublikováno')}
                    </span>
                  )}
              onToggle={(enabled) =>
                togglePublish.mutateAsync({ target: r.key, enabled })}
              onError={(e) => toast.error(String((e as Error)?.message ?? e))}
            />
            {r.domain && (
              <div className='flex items-center gap-2 pl-6 text-xs text-muted-foreground'>
                <code className='truncate'>{r.domain}</code>
                {r.published && (
                  <button type='button' onClick={() => copy(r.domain!)} title={t('common.copy', 'Kopírovat')}>
                    <Copy size={12} />
                  </button>
                )}
                {r.published && r.url && (
                  <a href={r.url} target='_blank' rel='noreferrer' title={r.url}>
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
            )}
            {r.key === 'mcp' && !r.published && (
              <p className='pl-6 text-[11px] text-muted-foreground'>
                {t(
                  'codable.mcpUnpublishedHint',
                  'Z IDE se sem nenapojíš, dokud MCP nezveřejníš.',
                )}
              </p>
            )}
            {/* SSO jen u UI — MCP je strojová linka (API klíč / mTLS). */}
            {r.key === 'ui' && r.published && (
              <div className='pl-6'>
                <AsyncStatusToggle
                  value={st.ui_sso_enabled === true}
                  actual={st.ui_sso_registered === true}
                  pending={toggleSso.isPending}
                  label={
                    <span className='flex items-center gap-2 text-sm'>
                      <Shield size={13} /> {t('codable.ssoLabel', 'Přihlášení přes SSO')}
                    </span>
                  }
                  renderIdleBadge={(on) =>
                    !on
                      ? null
                      : st.ui_sso_registered === true
                      ? (
                        <span className='text-xs text-emerald-400'>
                          {t('codable.ssoActive', 'aktivní')}
                        </span>
                      )
                      : (
                        <span className='text-xs text-amber-400'>
                          {t('codable.ssoPending', 'klient nezaregistrován')}
                        </span>
                      )}
                  onToggle={(enabled) => toggleSso.mutateAsync(enabled)}
                  onError={(e) => toast.error(String((e as Error)?.message ?? e))}
                />
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
